import type { ReactNode } from "react";
import type {
  AssessmentMethodology,
  AssessmentReport,
  AssetType,
  Intervention,
  InterventionType,
  NotReadyForSaleReason,
  Pillars,
  SatelliteImage,
} from "../types";

export type SolutionFormDateValue = number | string | null;

export type InterventionWithCounter = Intervention & {
  interventionCounter: number;
};

export type SatelliteImageFormValue = SatelliteImage & {
  satelliteImageCounter: number;
};

export type SatelliteImageFieldName = "mediaLink" | "caption" | "source";
export type InterventionFieldName = "action" | "ecosystem";
export type CounterAwareFieldError =
  string | { message: string; outcomeCounter?: number };
export type InterventionErrorItem = Partial<
  Record<InterventionFieldName, CounterAwareFieldError>
>;
export type InterventionTouchedItem = Partial<
  Record<InterventionFieldName, boolean>
>;
export type InterventionFieldConfig = {
  fieldName: InterventionFieldName;
  options: Array<string>;
};

export type SatelliteImageInputProps = {
  value: SatelliteImageFormValue;
  index: number;
  remove: () => void;
};

export type InterventionInputProps = {
  value: InterventionWithCounter;
  index: number;
  remove: () => void;
};

export type AddAnotherRenderProps<Value> = {
  value: Value;
  index: number;
  remove: () => void;
};

export type AddAnotherProps<Value> = {
  name: string;
  addLabel: string;
  getCounter: (value: Value) => number;
  createValue: (counter: number, values: Array<Value>) => Value;
  renderItem: (props: AddAnotherRenderProps<Value>) => ReactNode;
  compareValues?: (firstValue: Value, secondValue: Value) => number;
  initialCounter?: number;
  beforeAddContent?: ReactNode;
};

export const baseOutcomeOptions = [
  "Tonnes CO2",
  "m2",
  "Tree(s)",
  "m3 Kelp",
  "Biodiversity",
  "Minutes of monitoring footage",
  "Monitoring images",
  "Monitoring stations",
] as const;

export type OutcomeType = {
  unit: string;
  value: string;
  "unit-of-sale": boolean;
  "outcome-start-date": number | null;
  "outcome-end-date": number | null;
  outcomeCounter: number;
  origin?: "saved" | "draft";
};

export type OutcomeInputProps = {
  value: OutcomeType;
  index: number;
  remove: () => void;
};

export type ProgrammeInfoFormValues = {
  "carbon-crediting-programme": Array<string>;
  "carbon-accounting-methodology": string;
  "description-document": string;
  "asset-type": AssetType | null;
};

export type InternalDetailsFormValues = {
  "website-url": string;
  "xero-account-number": string;
  "research-team-notes": string;
  "additional-resources-link": string;
};

export type RiskMonitoringFormValues = {
  "is-risk-alert-enabled": boolean;
  "buffer-value-km": number;
  "geospatial-file": File | null;
  "geospatial-id"?: string | null;
};

export type SolutionIdentityFormValues = {
  "solution-name": string;
  "solution-vbc-website-display-name": string;
  "solution-developer": string;
  "solution-short-description": string;
};

export type SolutionClassificationFormValues = {
  "country-select": Array<string> | null;
  "solution-development-stage": Array<string>;
  "solution-assessment-methodology": AssessmentMethodology | null;
  "solution-intervention-type": InterventionType | null;
  "solution-is-ready-for-sale": boolean;
  "solution-not-ready-for-sale-reason": NotReadyForSaleReason | null;
  "solution-sdg": Array<number>;
};

export type CommercialInformationFormValues = {
  "minimum-order"?: number | string | null;
};

export type NarrativeFormValues = {
  "project-information": string;
  "area-through-time": string;
  "good-for-people": string;
  "good-for-earth": string;
};

export type SolutionLocationAndDateFormValues = {
  "solution-start-date": SolutionFormDateValue;
  "solution-end-date": SolutionFormDateValue;
  latitude: string;
  longitude: string;
  "location-pin-map-link": string;
};

export type SolutionImpactFormValues = {
  "solution-is-carbon-offset": boolean;
  interventions: Array<InterventionWithCounter>;
  outcomes: Array<OutcomeType>;
  pillars?: Pillars;
  "satellite-imagery": Array<SatelliteImageFormValue>;
};

export type AssessmentReportRichTextFieldName = `assessmentReport.${Exclude<
  keyof AssessmentReport,
  "benefits"
>}`;

export type CollapsibleFormSectionProps = {
  open: boolean;
};

export type AssessmentReportTextFieldProps = {
  name: string;
  label: string;
  multiline?: boolean;
  showValidationError?: boolean;
};

export type UniqueBenefitsInputsProps = {
  showEmptyValidation?: boolean;
};

export type UnitOfSaleOutcome = Pick<OutcomeType, "unit" | "unit-of-sale">;
export type OutcomeForValidation = Partial<OutcomeType>;
export type IndexedOutcomeForValidation = {
  outcome: OutcomeForValidation;
  index: number;
};

export type WriteableSolutionFormContainerProps = {
  handleCancel: () => void;
  submitLoading: boolean;
  submitDisabled: boolean;
  title: string;
  assessmentMethodologyField: ReactNode;
  interventionTypeField: ReactNode;
  programmeInfoSection: ReactNode;
  actionsMarginBottom?: number;
};
